import { adminHandlers } from "@/mock-api/adminHandlers.ts";
import {
    exampleInvoiceProcessor,
    exampleStripeProcessor,
    exampleVisaId,
    makeAdditionalCardData,
} from "@/mock-api/data/billingAccountCardsExample.ts";
import { createFieldValues } from "@/mock-api/data/billingAccountExample.ts";
import { defaultBillingAccountId } from "@/mock-api/data/billingAccountIds.ts";
import { countryRegionsConfigExample } from "@/mock-api/data/countryRegionsConfigExample.ts";
import { dbServiceDefaultProperties } from "@/mock-api/data/servicesExample.ts";
import { defaultStorageAccountUuid } from "@/mock-api/data/storageExample.ts";
import { getMockDb, markDirty } from "@/mock-api/db.ts";
import type { LocationSpecificData } from "@/mock-api/defaultDb.ts";
import { graphqlHandlers } from "@/mock-api/graphqlHandlers.ts";
import { metalHandlers } from "@/mock-api/metalHandlers.ts";
import { getMockTimeEpoch, getMockTimeIso, getMockTimeTz } from "@/mock-api/mockTime.ts";
import type { StandardRequest } from "@/mock-api/msw/StandardRequest.ts";
import { apiPrefix, http } from "@/mock-api/msw/handlerMiddleware.ts";
import { mustFindBy } from "@/mock-api/mustFindBy.ts";
import payment_return_htm_url from "@/mock-api/payment_return.htm?no-inline";
import payment_verify_htm_url from "@/mock-api/payment_verify.htm?no-inline";
import {
    randomAlphaNumeric,
    randomLowerAlpha,
    randomMac,
    randomPrivateIpv4,
    randomPublicIpv4,
    randomSubnetIpv4,
    randomSubnetIpv6,
    randomUpperAlphaNumeric,
    randomUuid,
} from "@/mock-api/randomTypes.ts";
import {
    generateId,
    generateUuid,
    getLocationDb,
    getParam,
    getQueryParams,
    getUserId,
    getUuidParam,
    parseBodyParams,
    parseForm,
    parseJson,
    throwStandardErrorResponse,
    validateSpec,
} from "@/mock-api/requestUtils.ts";
import { parseOmiseMockToken } from "@/modules/billing/omise/mockOmise.ts";
import type { OmiseChargeData } from "@/modules/billing/omise/parseOmisePersistentData.ts";
import { mockVerifyPageMarker } from "@/modules/billing/popup/popup.mock.ts";
import { isDbService } from "@/modules/services/utils.ts";
import { isStorybook } from "@/utils/environment.ts";
import type {
    AccessDelegation,
    AccessDelegationImpersonation,
    ApiToken,
    AutomatedTopUpEnableDisableResponse,
    BillingAccount,
    BillingAccountCard,
    BillingAccountCardData,
    Bucket,
    CampaignActivateResponse,
    CardVerifyResponse,
    Disk,
    FinalAmountResponse,
    Firewall,
    FirewallAssignedResource,
    IpAddress,
    IpAddressResourceType,
    LbForwardingRule,
    LbTarget,
    LinkParams,
    LinkSendDataResponse,
    LoadBalancer,
    ManagedService,
    ManagedServicesVmBody,
    ModifyLowBalanceNoticeSettingsResponse,
    OmiseCreditBuyResponse,
    PersistentDataResponse,
    RestrictionLevelClearTopupThreshold,
    SshKey,
    StorageAccountKey,
    UserMfaResponse,
    UserRoleResponse,
    VirtualMachine,
    VirtualMachineStorage,
    VirtualMachineStorageReplica,
    Vpc,
} from "@warrenio/api-spec/spec.oats.gen";
import * as accessSpec from "@warrenio/api-spec/spec/access";
import * as billingSpec from "@warrenio/api-spec/spec/billing";
import { InvoiceStatusEnum } from "@warrenio/api-spec/spec/billingEnums";
import * as diskSpec from "@warrenio/api-spec/spec/disks";
import * as networkSpec from "@warrenio/api-spec/spec/network";
import * as serviceSpec from "@warrenio/api-spec/spec/services";
import { ServiceWhitelistEntryBody } from "@warrenio/api-spec/spec/services";
import * as sshKeysSpec from "@warrenio/api-spec/spec/ssh_keys";
import * as storageSpec from "@warrenio/api-spec/spec/storage";
import { bucket_name } from "@warrenio/api-spec/spec/storage";
import * as vmSpec from "@warrenio/api-spec/spec/vm";
import { findById } from "@warrenio/utils/collections/findById";
import { mustGet } from "@warrenio/utils/collections/mustGet";
import { isObjectWithKey } from "@warrenio/utils/isObject";
import { notNull } from "@warrenio/utils/notNull";
import sleep from "@warrenio/utils/promise/sleep";
import { exhaustiveSwitchCheck, unreachableSwitchCase } from "@warrenio/utils/unreachable";
import { type GraphQLHandler, type HttpHandler, HttpResponse } from "msw";
import { first, last } from "remeda";
import invariant from "tiny-invariant";
import z from "zod";
import { zfd } from "zod-form-data";

/** Used in mock service worker registration to filter requests */
export { apiPrefix };

/* eslint-disable @typescript-eslint/require-await */

async function getChangeVmRunningStatusHandler(db: LocationSpecificData, uuid: string, status: "stopped" | "running") {
    const vm = mustFindBy(db.vms, (v) => v.uuid, uuid, "VM");
    vm.status = status;
    markDirty();

    return HttpResponse.json(vm);
}

// XXX: Hack for payment verification, should be stored in mock DB actually
const verifiedTopUpIds = new Set<string>();
const invoicePaymentsByDataRef = new Map<string, number>();

export const handlers: (HttpHandler | GraphQLHandler)[] = [
    //#region Compute
    http.get("/{location}/user-resource/vm/list", async (r) => {
        return HttpResponse.json(getLocationDb(r).vms);
    }),

    // NB: Need to use `Promise<Response>` since TypeScript can not infer a `StrictResponse` type
    //     if there are multiple different return statements.
    http.post("/{location}/user-resource/vm", async (r) => {
        const {
            billing_account_id = defaultBillingAccountId,
            designated_pool_uuid,
            os_name,
            os_version,
            description,
            name,
            username,
            ram,
            disks,
            vcpu,
            boot_firmware = "bios",
            network_uuid,
            ...body
        } = await parseBodyParams(r, vmSpec.VmCreateBody);

        const db = getLocationDb(r);
        const designatedPool = designated_pool_uuid
            ? mustFindBy(db.hostPools, (p) => p.uuid, designated_pool_uuid, "Designated host pool")
            : (db.hostPools.find((p) => p.is_default_designated) ?? notNull(db.hostPools[0], "at least one host pool"));

        const _network = network_uuid
            ? mustFindBy(db.vpcNetworks, (n) => n.uuid, network_uuid, "VPC network")
            : undefined;

        const created_at = getMockTimeTz();

        const user_id = getUserId(r);

        const vmStorage: VirtualMachineStorage = {
            created_at,
            name: "vda",
            pool: "n/a",
            primary: true,
            replica: [],
            shared: false,
            size: notNull(disks, "disks"),
            type: "n/a",
            user_id,
            uuid: generateUuid(),
        };

        const vm: VirtualMachine = {
            designated_pool_uuid,
            backup: false,

            current_pool_name: designatedPool.name,
            current_pool_uuid: designatedPool.uuid,
            designated_pool_name: designatedPool.name,

            hostname: randomAlphaNumeric(),
            mac: randomMac(),
            private_ipv4: randomPrivateIpv4(),
            storage: [vmStorage],
            user_id,

            os_name,
            os_version,
            boot_firmware,
            description,
            name,
            username: notNull(username, "username"),
            vcpu,

            created_at,
            updated_at: created_at,
            memory: ram,
            uuid: generateUuid(),
            status: "creating",
            billing_account: billing_account_id,
        };
        console.debug("Creating mock VM: %o", { body, vm });

        validateSpec(vmSpec.VirtualMachine, vm);

        db.vms.push(vm);
        markDirty();

        // Pretend to take some time to create the VM
        await sleep(3000);

        vm.status = "running";
        markDirty();

        return HttpResponse.json(vm);
    }),

    http.patch("/{location}/user-resource/vm", async (r) => {
        const { uuid, name, ram, vcpu, boot_firmware } = await parseJson(r, vmSpec.VmModifyBody);

        const db = getLocationDb(r);
        const vm = mustFindBy(db.vms, (v) => v.uuid, uuid, "VM");

        if (name !== undefined) vm.name = name;
        if (ram !== undefined) vm.memory = ram;
        if (vcpu !== undefined) vm.vcpu = vcpu;
        if (boot_firmware !== undefined) vm.boot_firmware = boot_firmware;

        markDirty();

        return HttpResponse.json(vm);
    }),

    // change vm user password
    http.patch("/{location}/user-resource/vm/user", async (_r) => {
        // nothing needs to be done here as password is never exposed to front
        return HttpResponse.json({ success: true });
    }),

    http.post("/{location}/user-resource/vm/boot_iso_media", async (r) => {
        const { uuid } = await parseJson(r, vmSpec.VmStartCustomRescueBody);

        const db = getLocationDb(r);
        const vm = mustFindBy(db.vms, (v) => v.uuid, uuid, "VM");
        vm.status = "running";

        markDirty();
        return HttpResponse.json(vm);
    }),

    http.post("/{location}/user-resource/vm/reinstall", async (r) => {
        const { uuid, os_name, os_version } = await parseJson(r, vmSpec.VmReinstallBody);
        const db = getLocationDb(r);

        const vm = mustFindBy(db.vms, (v) => v.uuid, uuid, "VM");
        vm.os_name = os_name;
        vm.os_version = os_version;

        markDirty();
        return HttpResponse.json(vm);
    }),

    //#region VM disk actions

    // Add disk to VM
    http.post("/{location}/user-resource/vm/storage", async (r) => {
        const { uuid, size_gb } = await parseJson(r, vmSpec.VmAddDiskBody);
        const db = getLocationDb(r);
        const vm = mustFindBy(db.vms, (vm) => vm.uuid, uuid, "VM");

        //take last disk and increment it
        const lastDisk = last(vm.storage);
        const newName = lastDisk ? `vd${String.fromCharCode(lastDisk.name.charCodeAt(2) + 1)}` : "vda";

        const newDisk: VirtualMachineStorage = {
            name: newName,
            pool: "n/a",
            shared: false,
            type: "n/a",
            user_id: getUserId(r),
            uuid: generateUuid(),
            size: size_gb,
            created_at: getMockTimeTz(),
            primary: false,
        };
        console.debug("Adding disk to VM: %o", { uuid, newDisk });

        vm.storage.push(newDisk);
        markDirty();

        return HttpResponse.json(newDisk, { status: 201 });
    }),

    // Resize disk
    http.patch("/{location}/user-resource/vm/storage", async (r) => {
        const { uuid, disk_uuid, size_gb } = await parseJson(r, vmSpec.VmResizeDiskBody);
        const db = getLocationDb(r);
        const vm = mustFindBy(db.vms, (vm) => vm.uuid, uuid, "VM");

        const disk = vm.storage.find((disk) => disk.uuid === disk_uuid);
        if (!disk) {
            throwStandardErrorResponse(`Disk with uuid ${disk_uuid} not found`);
        }
        if (disk.size > size_gb) {
            throwStandardErrorResponse("Shrinking disk is not supported");
        }

        disk.size = size_gb;
        markDirty();

        return HttpResponse.json(disk);
    }),

    // Delete disk from VM
    http.delete("/{location}/user-resource/vm/storage", async (r) => {
        const { uuid, storage_uuid } = await parseJson(r, vmSpec.VmDeleteDiskBody);
        const db = getLocationDb(r);
        const vm = mustFindBy(db.vms, (vm) => vm.uuid, uuid, "VM");

        const disk = vm.storage.find((disk) => disk.uuid === storage_uuid);
        if (!disk) {
            throwStandardErrorResponse(`Disk with uuid ${storage_uuid} not found`);
        }

        if (disk.primary) {
            throwStandardErrorResponse("Primary disk can not be deleted");
        }

        vm.storage = vm.storage.filter((disk) => disk.uuid !== storage_uuid);
        markDirty();

        return HttpResponse.json({ success: true });
    }),

    // Attach disk to VM
    http.post("/{location}/user-resource/vm/storage/attach", async (r) => {
        const { uuid, storage_uuid } = await parseJson(r, diskSpec.DiskAttachDetachBody);
        const db = getLocationDb(r);

        const vm = mustFindBy(db.vms, (v) => v.uuid, uuid, "VM");
        const disk = mustFindBy(db.disks, (d) => d.uuid, storage_uuid, "Disk");

        // Check if disk is already attached to this VM
        const existingAttachment = vm.storage.find((s) => s.uuid === storage_uuid);
        if (existingAttachment) {
            throwStandardErrorResponse(`Disk ${storage_uuid} is already attached to VM ${uuid}`);
        }

        // Find next available device name
        const existingNames = vm.storage.map((s) => s.name).sort();
        let nextName = "vda";
        for (const name of existingNames) {
            if (name === nextName) {
                nextName = `vd${String.fromCharCode(nextName.charCodeAt(2) + 1)}`;
            }
        }

        const vmStorage: VirtualMachineStorage = {
            uuid: storage_uuid,
            name: nextName,
            size: disk.size_gb,
            primary: false,
            type: "n/a",
            replica: [],
            pool: "n/a",
            shared: false,
            user_id: getUserId(r),
            created_at: getMockTimeTz(),
        };

        vm.storage.push(vmStorage);
        markDirty();

        return HttpResponse.json(vmStorage);
    }),

    // Detach disk from VM
    http.post("/{location}/user-resource/vm/storage/detach", async (r) => {
        const { uuid, storage_uuid } = await parseJson(r, diskSpec.DiskAttachDetachBody);

        const db = getLocationDb(r);
        const vm = mustFindBy(db.vms, (v) => v.uuid, uuid, "VM");

        const storageIndex = vm.storage.findIndex((s) => s.uuid === storage_uuid);
        if (storageIndex === -1) {
            throwStandardErrorResponse(`Disk ${storage_uuid} is not attached to VM ${uuid}`);
        }

        const storage = vm.storage[storageIndex];
        if (storage.primary) {
            throwStandardErrorResponse("Cannot detach primary disk");
        }

        vm.storage.splice(storageIndex, 1);
        markDirty();

        return HttpResponse.json({ success: true });
    }),

    //#endregion VM disk actions

    //#region VM snapshot actions

    // Create snapshot
    http.post("/{location}/user-resource/vm/replica", async (r) => {
        const { uuid } = await parseJson(r, vmSpec.VmReplicaCreateBody);

        const db = getLocationDb(r);
        const vm = mustFindBy(db.vms, (vm) => vm.uuid, uuid, "VM");

        const primaryStorage = vm.storage.find((disk) => disk.primary);
        if (!primaryStorage) {
            throwStandardErrorResponse("Primary disk not found");
        }

        const snapshot: VirtualMachineStorageReplica = {
            created_at: getMockTimeTz(),
            master_uuid: primaryStorage.uuid,
            pool: "",
            size: primaryStorage.size,
            type: "snapshot",
            uuid: generateUuid(),
        };
        console.debug("Creating snapshot for VM: %o", { uuid, snapshot });

        const replicas = primaryStorage.replica;
        if (!replicas) {
            throwStandardErrorResponse("Replicas not found");
        }
        replicas.push(snapshot);
        markDirty();

        return HttpResponse.json(primaryStorage, { status: 201 });
    }),

    // Delete snapshot
    http.delete("/{location}/user-resource/vm/replica", async (r) => {
        const { replica_uuid } = await parseJson(r, vmSpec.VmReplicaDeleteBody);

        const db = getLocationDb(r);
        //get vm with replica
        const vm = db.vms.find((vm) =>
            vm.storage.some((disk) => disk.replica!.some((replica) => replica.uuid === replica_uuid)),
        );

        if (!vm) {
            throwStandardErrorResponse("VM not found");
        }

        const primaryStorage = vm.storage.find((disk) => disk.primary);
        if (!primaryStorage) {
            throwStandardErrorResponse("Primary disk not found");
        }

        const snapshot = primaryStorage.replica!.find((replica) => replica.uuid === replica_uuid);
        if (!snapshot) {
            throwStandardErrorResponse(`Snapshot with uuid ${replica_uuid} not found`);
        }

        primaryStorage.replica = primaryStorage.replica!.filter((replica) => replica.uuid !== replica_uuid);
        markDirty();

        return HttpResponse.json({ success: true });
    }),

    // Restore snapshot
    http.post("/{location}/user-resource/vm/rebuild", async (r) => {
        const { uuid, replica_uuid } = await parseJson(r, vmSpec.VmReplicaRebuildBody);
        const db = getLocationDb(r);
        const vm = mustFindBy(db.vms, (vm) => vm.uuid, uuid, "VM");

        const primaryStorage = vm.storage.find((disk) => disk.primary);
        if (!primaryStorage) {
            throwStandardErrorResponse("Primary disk not found");
        }

        const snapshot = primaryStorage.replica!.find((replica) => replica.uuid === replica_uuid);
        if (!snapshot) {
            throwStandardErrorResponse(`Snapshot with uuid ${replica_uuid} not found`);
        }

        //restore snapshot, update updated_at
        vm.updated_at = getMockTimeTz();
        markDirty();

        return HttpResponse.json(vm, { status: 201 });
    }),

    //#endregion VM snapshot actions

    //#region VM backup actions

    // enable/disable VM backups
    http.post("/{location}/user-resource/vm/backup", async (r) => {
        const { uuid } = await parseJson(r, vmSpec.VmBackupEnableDisableBody);
        const db = getLocationDb(r);
        const vm = mustFindBy(db.vms, (vm) => vm.uuid, uuid, "VM");
        vm.backup = !vm.backup;
        markDirty();

        return HttpResponse.json(vm);
    }),

    //#endregion VM backup actions

    http.post("/{location}/user-resource/vm/clone", async (r) => {
        const { uuid, name } = await parseJson(r, vmSpec.VmCloneBody);
        const db = getLocationDb(r);
        const sourceVm = mustFindBy(db.vms, (vm) => vm.uuid, uuid, "VM");

        const created_at = getMockTimeTz();

        const clonedStorage = sourceVm.storage.map((s) => ({
            ...s,
            uuid: generateUuid(),
            created_at,
        }));

        const clonedVmUuid = generateUuid();
        const clonedVm: VirtualMachine = {
            ...sourceVm,
            name,
            uuid: clonedVmUuid,
            created_at,
            updated_at: created_at,
            hostname: randomAlphaNumeric(),
            mac: randomMac(),
            private_ipv4: randomPrivateIpv4(),
            status: "creating",
            storage: clonedStorage,
        };

        getLocationDb(r).vms.push(clonedVm);

        const sourceVmAttachedIp = db.ipAddresses.find(
            (ip) => ip.assigned_to_resource_type === "virtual_machine" && ip.assigned_to === uuid,
        );
        if (sourceVmAttachedIp) {
            // create new IP and attach it to a cloned VM
            const newIp: IpAddress = {
                ...sourceVmAttachedIp,
                uuid: generateUuid(),
                address: randomPrivateIpv4(),
                created_at,
                updated_at: created_at,
                name: sourceVmAttachedIp.name !== "" ? `${sourceVmAttachedIp.name} clone` : "",
                assigned_to: clonedVmUuid,
            };
            db.ipAddresses.push(newIp);
        }

        markDirty();

        // Pretend to take some time to create the VM
        await sleep(3000);
        clonedVm.status = "running";
        markDirty();

        return HttpResponse.json(clonedVm);
    }),

    http.post("/{location}/user-resource/vm/start", async (r) => {
        const { uuid } = await parseJson(r, vmSpec.VmDeleteBody);
        const db = getLocationDb(r);
        return await getChangeVmRunningStatusHandler(db, uuid, "running");
    }),

    http.post("/{location}/user-resource/vm/stop", async (r) => {
        const { uuid } = await parseJson(r, vmSpec.VmDeleteBody);
        const db = getLocationDb(r);
        return await getChangeVmRunningStatusHandler(db, uuid, "stopped");
    }),

    http.delete("/{location}/user-resource/vm", async (r) => {
        const { uuid } = await parseJson(r, vmSpec.VmDeleteBody);
        const db = getLocationDb(r);

        const attachedIp = db.ipAddresses.find(
            (ip) => ip.assigned_to_resource_type === "virtual_machine" && ip.assigned_to === uuid,
        );
        if (attachedIp) {
            unAssignIp(attachedIp);
        }

        const vms = db.vms.filter((vm) => vm.uuid !== uuid);
        db.vms = vms;
        markDirty();

        return HttpResponse.json({ success: true });
    }),

    http.get("/api/parameters/vm", async (_r) => {
        return HttpResponse.json(getMockDb().vmParameters);
    }),
    //#endregion Compute

    //#region Storage
    //#region queries
    http.get("/storage/accounts", async (_r) => {
        return HttpResponse.json(getMockDb().storageAccounts);
    }),

    http.get("/storage/accounts/{uuid}/keys", async (_r) => {
        return HttpResponse.json(getMockDb().storageKeys);
    }),

    http.patch("/storage/accounts/{uuid}", async (r) => {
        const uuid = getUuidParam(r);
        const { title, billing_account_id } = await parseForm(r, storageSpec.StorageAccountUpdateBody);

        const account = getMockDb().storageAccounts.find((i) => i.uuid === uuid)!;
        if (billing_account_id) account.billing_account_id = billing_account_id;
        if (title) account.title = title;

        markDirty();

        return HttpResponse.json(account);
    }),

    http.get("/storage/bucket/list", async (_r) => {
        return HttpResponse.json(getMockDb().storages);
    }),

    http.get("/storage/user/keys", async (_r) => {
        return HttpResponse.json(getMockDb().storageKeys);
    }),

    http.get("/storage/api/s3", async (_r) => {
        return HttpResponse.json(getMockDb().s3Url);
    }),

    http.put("/storage/bucket/cors", async (_r) => {
        return HttpResponse.xml("<mock />");
    }),

    http.put("/storage/bucket", async (r) => {
        const { storage_account_uuid = defaultStorageAccountUuid, name } = await parseForm(
            r,
            storageSpec.BucketCreateBody,
        );

        const storageBucket: Bucket = {
            storage_account_uuid,
            name,
            size_bytes: 0,
            billing_account_id: defaultBillingAccountId,
            num_objects: 0,
            created_at: getMockTimeIso(),
        };

        console.log(storageBucket);

        console.debug("Creating mock bucket: %o", storageBucket);

        validateSpec(storageSpec.Bucket, storageBucket);

        getMockDb().storages.push(storageBucket);
        markDirty();

        return HttpResponse.json(storageBucket);
    }),

    http.patch("/storage/bucket", async (r) => {
        const { name, billing_account_id, storage_account_uuid } = await parseForm(r, storageSpec.BucketUpdateBody);

        const bucket = getMockDb().storages.find((i) => i.name === name)!;
        if (storage_account_uuid && storage_account_uuid !== bucket.storage_account_uuid) {
            return HttpResponse.json(
                { message: "Moving buckets between storage accounts is not supported." },
                { status: 403 },
            );
        }
        if (billing_account_id) bucket.billing_account_id = billing_account_id;
        if (bucket.billing_account_id_override) delete bucket.billing_account_id_override;

        console.debug("Updating mock bucket: %o", bucket);

        validateSpec(storageSpec.Bucket, bucket);

        markDirty();

        return HttpResponse.json(bucket);
    }),

    http.delete("/storage/bucket", async (r) => {
        const { name } = await parseForm(r, z.object({ name: bucket_name }));
        getMockDb().storages = getMockDb().storages.filter((key) => key.name !== name);
        markDirty();
        return HttpResponse.json({ success: true });
    }),
    //#endregion queries

    //#region mutations
    http.delete("/storage/accounts/{uuid}/keys/{access_key}", async (r) => {
        const access_key = getParam(r, "access_key");
        getMockDb().storageKeys = getMockDb().storageKeys.filter((key) => key.access_key !== access_key);
        markDirty();
        return HttpResponse.json({ success: true });
    }),
    http.patch("/storage/accounts/{uuid}/keys/{access_key}", async (r) => {
        const access_key = getParam(r, "access_key");
        const { description } = await parseJson(r, storageSpec.StorageAccountKeyCreateBody);
        const key = getMockDb().storageKeys.find((i) => i.access_key === access_key)!;
        key.description = description;
        markDirty();
        return HttpResponse.json(key);
    }),
    http.post("/storage/accounts/{uuid}/keys", async (r) => {
        const { description, read_only } = await parseJson(r, storageSpec.StorageAccountKeyCreateBody);
        const key: StorageAccountKey = {
            access_key: randomUpperAlphaNumeric(20),
            secret_key: randomAlphaNumeric(40),
            read_only: !!read_only,
            description,
        };
        getMockDb().storageKeys.push(key);
        markDirty();
        return HttpResponse.json(key);
    }),
    //#endregion mutations

    //#region Disks
    http.get("/{location}/storage/disks", async (r) => {
        const db = getLocationDb(r);
        return HttpResponse.json(db.disks);
    }),

    http.post("/{location}/storage/disks", async (r) => {
        const db = getLocationDb(r);
        const defaultHostPool = db.hostPools.find((pool) => pool.is_default_designated) ?? notNull(first(db.hostPools));

        const {
            billing_account_id,
            size_gb = 20,
            source_image,
            source_image_type = "EMPTY",
            display_name,
            storage_pool_uuid = defaultHostPool?.storage_pool_uuid,
        } = await parseForm(r, diskSpec.DiskCreateBody);

        const disk: Disk = {
            uuid: generateUuid(),
            user_id: getUserId(r),
            billing_account_id,
            status: "Active",
            size_gb,
            source_image,
            source_image_type,
            created_at: getMockTimeIso(),
            display_name,
            storage_pool_uuid,
            snapshots: [],
        };

        validateSpec(diskSpec.Disk, disk);
        getLocationDb(r).disks.push(disk);
        markDirty();

        return HttpResponse.json(disk);
    }),

    http.get("/{location}/storage/disks/{uuid}", async (r) => {
        const uuid = getUuidParam(r);
        const disk = mustFindBy(getLocationDb(r).disks, (d) => d.uuid, uuid, "Disk");
        return HttpResponse.json(disk);
    }),

    http.patch("/{location}/storage/disks/{uuid}", async (r) => {
        const uuid = getUuidParam(r);
        const updates = await parseForm(r, diskSpec.DiskUpdateBody);

        const disk = mustFindBy(getLocationDb(r).disks, (d) => d.uuid, uuid, "Disk");

        if (updates.display_name !== undefined) {
            disk.display_name = updates.display_name;
        }
        if (updates.billing_account_id !== undefined) {
            disk.billing_account_id = updates.billing_account_id;
        }

        disk.updated_at = getMockTimeIso();

        validateSpec(diskSpec.Disk, disk);
        markDirty();

        return HttpResponse.json(disk);
    }),

    http.delete("/{location}/storage/disks/{uuid}", async (r) => {
        const uuid = getUuidParam(r);
        const db = getLocationDb(r);

        const diskIndex = db.disks.findIndex((d) => d.uuid === uuid);
        if (diskIndex === -1) {
            throwStandardErrorResponse(`Disk with uuid ${uuid} not found`);
        }

        db.disks.splice(diskIndex, 1);
        markDirty();

        return HttpResponse.json({ success: true });
    }),
    //#endregion Disks

    //#endregion Storage

    //#region Services
    //#region queries
    http.get("/user-resource/service/packages", async (_r) => {
        return HttpResponse.json(getMockDb().services);
    }),

    http.post("/user-resource/service/package", async (r) => {
        const {
            billing_account_id,
            display_name,
            is_multi_node,
            package_parameters: { location },
            reserve_public_ip,
            service,
            version,
            vm_cpu,
            vm_disk_gb,
            vm_ram,
        } = await parseJson(r, serviceSpec.ManagedServiceCreateBody);

        // PS! handle kubernetes variant also!
        if (isDbService(service)) {
            const created_at = getMockTimeTz();
            const defaultProperties = dbServiceDefaultProperties[service];
            const image = getMockDb().serviceImages.find((i) => i.service_name === service)!;

            const getVmResource = (): ManagedServicesVmBody => ({
                resource_allocation: {
                    memory: vm_ram,
                    vcpu: vm_cpu,
                    status: "running",
                    storage: [
                        {
                            primary: true,
                            size: vm_disk_gb,
                            uuid: generateUuid(),
                            replica: [],
                        },
                    ],
                },
                resource_id: generateUuid(),
                resource_location: location,
                resource_type: "vm",
            });

            const resources: ManagedServicesVmBody[] = [getVmResource()];
            if (is_multi_node) resources.push(getVmResource());

            if (reserve_public_ip) {
                resources.push({
                    resource_allocation: {},
                    resource_id: generateUuid(),
                    resource_location: location,
                    resource_type: "virtual_ip",
                });
            }

            const managedService: ManagedService = {
                uuid: generateUuid(),
                billing_account_id,
                created_at,
                updated_at: created_at,
                display_name,
                is_multi_node,
                is_deleted: false,
                service,
                version,
                user_id: getUserId(r),
                status: "active",
                properties: {
                    ...defaultProperties,
                    service_ip: randomPrivateIpv4(),
                    location,
                },
                prices: image.prices,
                resources,
            };
            console.debug("Creating mock service: %o", managedService);

            validateSpec(serviceSpec.ManagedService, managedService);

            getMockDb().services.push(managedService);
            markDirty();

            // Pretend to take some time to create the VM
            await sleep(4000);

            return HttpResponse.json(managedService);
        } else {
            console.warn("Only Db service mock is implemented right now, service selected: %o", { service });
            throwStandardErrorResponse("Only Db service mock is implemented right now");
        }
    }),

    http.get("/user-resource/service/configurations", async (_r) => {
        return HttpResponse.json(getMockDb().serviceImages);
    }),

    http.get("/user-resource/service/package/{uuid}/whitelist_addresses", async (_r) => {
        return HttpResponse.json(getMockDb().serviceWhitelist);
    }),

    http.post("/user-resource/service/package/{uuid}/whitelist_addresses", async (_r) => {
        getMockDb().serviceWhitelist.push(randomPrivateIpv4());
        markDirty();
        return HttpResponse.json(randomPrivateIpv4());
    }),

    http.delete("/user-resource/service/package/{uuid}/whitelist_addresses", async (r) => {
        const { ip_address } = await parseJson(r, ServiceWhitelistEntryBody);
        getMockDb().serviceWhitelist = getMockDb().serviceWhitelist.filter((d) => d !== ip_address);
        markDirty();
        return HttpResponse.json({ success: true });
    }),

    http.get("/user-resource/service/package/{uuid}/secrets", async (_r) => {
        return HttpResponse.json(getMockDb().serviceSecrets);
    }),

    //#endregion queries

    //#endregion Services

    http.get("/user-resource/ssh_keys", async (_r) => {
        return HttpResponse.json(getMockDb().sshKeys);
    }),
    http.post("/user-resource/ssh_keys", async (r) => {
        const { name, public_key } = await parseJson(r, sshKeysSpec.SshKeyCreateBody);

        const sshKey: SshKey = {
            created_at: getMockTimeTz(),
            name,
            public_key,
            user_id: getUserId(r),
            uuid: generateUuid(),
        };
        console.debug("Creating mock ssh key: %o", sshKey);

        validateSpec(sshKeysSpec.SshKey, sshKey);

        getMockDb().sshKeys.push(sshKey);
        markDirty();

        return HttpResponse.json(sshKey);
    }),
    http.patch("/user-resource/ssh_keys/{uuid}", async (r) => {
        const uuid = getUuidParam(r);
        const { name } = await parseJson(r, sshKeysSpec.SshKeyUpdateBody);

        const sshKey = mustFindBy(getMockDb().sshKeys, (item) => item.uuid, uuid, "SSH key");

        if (name !== undefined) {
            sshKey.name = name;
        }

        console.debug("Updating mock ssh key: %o", sshKey);

        validateSpec(sshKeysSpec.SshKey, sshKey);

        markDirty();

        return HttpResponse.json(sshKey);
    }),
    http.delete("/user-resource/ssh_keys/{uuid}", async (r) => {
        const uuid = getParam(r, "uuid");

        getMockDb().sshKeys = getMockDb().sshKeys.filter((t) => t.uuid !== uuid);
        markDirty();

        return HttpResponse.json({ success: true });
    }),

    http.get("/config/vm_images", async (_r) => {
        return HttpResponse.json(getMockDb().vmImages);
    }),

    http.get("/config/boot_images", async (_r) => {
        return HttpResponse.json(getMockDb().bootImages);
    }),

    //#region API Tokens
    http.get("/user-resource/token/list", async (_r) => {
        return HttpResponse.json(getMockDb().tokens);
    }),
    http.post("/user-resource/token", async (r) => {
        const {
            billing_account_id = defaultBillingAccountId,
            description,
            restricted = false,
        } = await parseForm(r, accessSpec.ApiTokenCreateBody);

        const apiToken: ApiToken = {
            billing_account_id,
            consumer_id: randomUuid(),
            created_at: getMockTimeTz(),
            description,
            id: generateId(),
            kong_id: randomUuid(),
            restricted,
            token: randomAlphaNumeric(32),
            user_id: getUserId(r),
        };
        console.debug("Creating mock token: %o", apiToken);

        validateSpec(accessSpec.ApiToken, apiToken);

        getMockDb().tokens.push(apiToken);
        markDirty();

        return HttpResponse.json(apiToken);
    }),
    http.patch("/user-resource/token", async (r) => {
        const { token_id, description, billing_account_id } = await parseForm(r, accessSpec.ApiTokenModifyBody);

        const apiToken = findById(getMockDb().tokens, token_id);
        if (description) apiToken.description = description;
        if (billing_account_id) apiToken.billing_account_id = billing_account_id;

        console.debug("Updating mock token: %o", apiToken);

        validateSpec(accessSpec.ApiToken, apiToken);

        markDirty();

        return HttpResponse.json(apiToken);
    }),
    http.delete("/user-resource/token", async (r) => {
        const { token_id } = await parseForm(r, accessSpec.ApiTokenDeleteBody);

        getMockDb().tokens = getMockDb().tokens.filter((t) => t.id !== token_id);
        markDirty();

        return HttpResponse.json({ success: true });
    }),
    //#endregion

    //#region Access Delegations
    http.get("/user-resource/access_group", async (_r) => {
        return HttpResponse.json(getMockDb().delegations);
    }),
    http.post("/user-resource/access_group", async (r) => {
        const { billing_account_id = defaultBillingAccountId, name } = await parseForm(
            r,
            accessSpec.AccessGroupCreateBody,
        );

        const created_at = getMockTimeTz();

        const delegation: AccessDelegation = {
            billing_account_id,
            created_at,
            updated_at: created_at,
            id: generateId(),
            impersonations: [],
            name,
            owner_id: getUserId(r),
        };
        validateSpec(accessSpec.AccessDelegation, delegation);

        getMockDb().delegations.push(delegation);
        markDirty();

        return HttpResponse.json(delegation);
    }),
    http.delete("/user-resource/access_group/{id}", async (r) => {
        const id = Number(r.params.id);

        getMockDb().delegations = getMockDb().delegations.filter((d) => d.id !== id);
        markDirty();

        return HttpResponse.json({ success: true });
    }),

    http.post("/user-resource/access_group/{id}/impersonation", async (r) => {
        const id = Number(r.params.id);
        const { grantee_username } = await parseForm(r, accessSpec.AccessDelegationImpersonationCreateBody);

        const impersonation: AccessDelegationImpersonation = {
            created_at: getMockTimeTz(),
            grantee_username,
            is_accepted: false,
            uuid: generateUuid(),
        };
        validateSpec(accessSpec.AccessDelegationImpersonation, impersonation);

        const delegation = findById(getMockDb().delegations, id);
        delegation.impersonations.push(impersonation);
        markDirty();

        return HttpResponse.json(impersonation);
    }),
    http.delete("/user-resource/access_group/{id}/impersonation/{impersonation_id}", async (r) => {
        const id = Number(getParam(r, "id"));
        const impersonation_id = getParam(r, "impersonation_id");

        const delegation = findById(getMockDb().delegations, id);
        delegation.impersonations = delegation.impersonations.filter((i) => i.uuid !== impersonation_id);
        markDirty();

        return HttpResponse.json({ success: true });
    }),
    //#endregion

    //#region Access impersonation
    http.get("/user-resource/impersonation", async (_r) => {
        return HttpResponse.json(getMockDb().impersonations);
    }),
    http.post("/user-resource/impersonation/{uuid}/start", async (r) => {
        const uuid = getUuidParam(r);
        const impersonation = mustFindBy(getMockDb().impersonations, (item) => item.uuid, uuid, "Impersonation");

        if (!impersonation.is_accepted) {
            throwStandardErrorResponse("Impersonation has not been accepted yet");
        }

        return HttpResponse.json({
            token: randomAlphaNumeric(32),
        });
    }),
    //#endregion

    //#region User & profile
    http.get("/user-resource/user", async (_r) => {
        return HttpResponse.json(getMockDb().users[0]);
    }),
    http.get("/user-resource/user/role", async (_r) => {
        return HttpResponse.json<UserRoleResponse>({ role: "admin" });
    }),

    http.get("/user-resource/mfa/is_active", async (_r) => {
        return HttpResponse.json<UserMfaResponse>({ success: false });
    }),
    //#endregion

    //#region IP Addresses
    http.get("/{location}/network/ip_addresses", async (r) => {
        return HttpResponse.json(getLocationDb(r).ipAddresses);
    }),
    http.post("/{location}/network/ip_addresses", async (r) => {
        const { billing_account_id = defaultBillingAccountId, name } = await parseJson(
            r,
            networkSpec.IpAddressCreateBody,
        );

        const created_at = getMockTimeTz();

        const ipAddress: IpAddress = {
            id: generateId(),
            address: randomPublicIpv4(),
            user_id: getUserId(r),
            billing_account_id,
            type: "public",
            enabled: true,
            created_at,
            updated_at: created_at,
            uuid: generateUuid(),
            is_deleted: false,
            is_ipv6: false,
            name,
        };
        console.debug("Creating mock IP: %o", { ipAddress });

        validateSpec(networkSpec.IpAddress, ipAddress);

        getLocationDb(r).ipAddresses.push(ipAddress);
        markDirty();

        return HttpResponse.json(ipAddress);
    }),
    http.post("/{location}/network/ip_addresses/{address}/assign", async (r) => {
        const body = await parseJson(r, networkSpec.IpAddressAssignBody);

        const db = getLocationDb(r);
        const ipAddress = getRequestIpAddress(r);

        let assignedObj:
            | {
                  assigned_to: string;
                  assigned_to_resource_type: IpAddressResourceType;
                  assigned_to_private_ip: string;
              }
            | undefined;

        if ("assigned_to_resource_type" in body) {
            const { assigned_to_resource_type, assigned_to } = body;
            invariant(assigned_to_resource_type !== undefined);

            switch (assigned_to_resource_type) {
                case "virtual_machine": {
                    const vm = mustFindBy(db.vms, (item) => item.uuid, assigned_to, "Virtual machine");
                    assignedObj = {
                        assigned_to: vm.uuid,
                        assigned_to_resource_type: "virtual_machine",
                        assigned_to_private_ip: vm.private_ipv4,
                    };
                    break;
                }
                case "service": {
                    const service = mustFindBy(getMockDb().services, (item) => item.uuid, assigned_to, "Service");
                    const serviceIp = "service_ip" in service.properties ? service.properties.service_ip : undefined;
                    invariant(serviceIp, `Service with UUID ${assigned_to} does not have service_ip`);
                    assignedObj = {
                        assigned_to: service.uuid,
                        assigned_to_resource_type: "service",
                        assigned_to_private_ip: serviceIp,
                    };
                    break;
                }
                case "load_balancer": {
                    const lb = mustFindBy(db.loadBalancers, (item) => item.uuid, assigned_to, "Load balancer");
                    assignedObj = {
                        assigned_to: lb.uuid,
                        assigned_to_resource_type: "load_balancer",
                        assigned_to_private_ip: lb.private_address,
                    };
                    break;
                }
                default:
                    exhaustiveSwitchCheck(assigned_to_resource_type);
            }
        } else {
            unreachableSwitchCase(body);
        }

        const old_ip = db.ipAddresses.find((ip) => ip.assigned_to === assignedObj.assigned_to);
        if (
            old_ip &&
            // Do not unassign if just re-assigned to the same IP
            old_ip.uuid !== ipAddress.uuid
        ) {
            unAssignIp(old_ip);
        }

        ipAddress.assigned_to = assignedObj.assigned_to;
        ipAddress.assigned_to_resource_type = assignedObj.assigned_to_resource_type;
        ipAddress.assigned_to_private_ip = assignedObj.assigned_to_private_ip;
        delete ipAddress.unassigned_at;

        markDirty();

        validateSpec(networkSpec.IpAddress, ipAddress);

        return HttpResponse.json(ipAddress);
    }),
    http.post("/{location}/network/ip_addresses/{address}/unassign", async (r) => {
        const ipAddress = getRequestIpAddress(r);
        unAssignIp(ipAddress);
        markDirty();

        validateSpec(networkSpec.IpAddress, ipAddress);

        return HttpResponse.json(ipAddress);
    }),
    http.patch("/{location}/network/ip_addresses/{address}", async (r) => {
        const { name, billing_account_id } = await parseJson(r, networkSpec.IpAddressModifyBody);

        const ipAddress = getRequestIpAddress(r);
        if (!name) {
            delete ipAddress.name;
        } else {
            ipAddress.name = name;
        }
        if (billing_account_id) ipAddress.billing_account_id = billing_account_id;
        markDirty();

        console.debug("Updating ip address: %o", ipAddress);

        validateSpec(networkSpec.IpAddress, ipAddress);

        return HttpResponse.json(ipAddress);
    }),
    http.delete("/{location}/network/ip_addresses/{address}", async (r) => {
        const address = getParam(r, "address");

        const db = getLocationDb(r);
        db.ipAddresses = db.ipAddresses.filter((ip) => ip.address !== address);
        markDirty();

        return HttpResponse.json(true);
    }),
    //#endregion

    //#region VPC Networks
    http.get("/{location}/network/virtual-machines/{uuid}/interfaces", async (r) => {
        const uuid = getParam(r, "uuid");
        return HttpResponse.json(getLocationDb(r).vmInterfaces.filter((i) => i.vm_uuid === uuid));
    }),

    http.get("/{location}/network/virtual-machines/interfaces", async (r) => {
        return HttpResponse.json(getLocationDb(r).vmInterfaces);
    }),

    http.get("/{location}/network/networks", async (r) => {
        return HttpResponse.json(getLocationDb(r).vpcNetworks);
    }),
    http.post("/{location}/network/network", async (r) => {
        const { name } = await parseForm(r, networkSpec.VpcCreateBody);

        const created_at = getMockTimeTz();

        const network: Vpc = {
            id: generateId(),
            subnet: randomSubnetIpv4(),
            name,
            created_at,
            updated_at: created_at,
            uuid: generateUuid(),
            type: "private",
            is_default: getLocationDb(r).vpcNetworks.length === 0,
            vm_uuids: [],
            resources_count: 0,
            subnet_ipv6: randomSubnetIpv6(),
        };
        console.debug("Creating mock VPC network: %o", { name });

        validateSpec(networkSpec.Vpc, network);

        getLocationDb(r).vpcNetworks.push(network);
        markDirty();

        return HttpResponse.json(network);
    }),
    http.patch("/{location}/network/network/{uuid}", async (r) => {
        const { name } = await parseJson(r, networkSpec.VpcUpdateBody);

        const vpcNetwork = getRequestVpcNetwork(r);
        if (name) vpcNetwork.name = name;
        markDirty();

        console.debug("Updating vpc network: %o", vpcNetwork);

        validateSpec(networkSpec.Vpc, vpcNetwork);

        return HttpResponse.json(vpcNetwork);
    }),
    http.put("/{location}/network/network/{uuid}/default", async (r) => {
        const vpcNetwork = getRequestVpcNetwork(r);
        const db = getLocationDb(r);
        db.vpcNetworks = db.vpcNetworks.map((n) => {
            return { ...n, is_default: n.uuid === vpcNetwork.uuid };
        });
        markDirty();

        return HttpResponse.json(true);
    }),
    http.delete("/{location}/network/network/{uuid}", async (r) => {
        const vpcNetwork = getRequestVpcNetwork(r);

        const db = getLocationDb(r);
        db.vpcNetworks = db.vpcNetworks.filter((n) => n.uuid !== vpcNetwork.uuid);
        markDirty();

        return HttpResponse.json(true);
    }),
    //#endregion

    //#region Load Balancers
    http.get("/{location}/network/load_balancers", async (r) => {
        return HttpResponse.json(getLocationDb(r).loadBalancers);
    }),
    http.post("/{location}/network/load_balancers", async (r) => {
        const { billing_account_id, display_name, network_uuid, rules, targets, reserve_public_ip } = await parseJson(
            r,
            networkSpec.LoadBalancerCreateBody,
        );

        const lbPrivateAddress = randomPrivateIpv4();
        const lbUuid = generateUuid();

        const created_at = getMockTimeTz();
        const lb: LoadBalancer = {
            uuid: lbUuid,
            display_name,
            user_id: getUserId(r),
            billing_account_id,
            created_at,
            updated_at: created_at,
            is_deleted: false,
            private_address: lbPrivateAddress,
            network_uuid,
            forwarding_rules: rules.map(({ source_port, target_port }) => ({
                protocol: "TCP",
                uuid: generateUuid(),
                created_at,
                source_port,
                target_port,
                settings: {
                    connection_limit: 10000,
                    session_persistence: "SOURCE_IP",
                },
            })),
            targets: targets.map(({ target_type, target_uuid }) => ({
                created_at,
                target_uuid,
                target_type,
                target_ip_address: randomPrivateIpv4(),
            })),
        };

        validateSpec(networkSpec.LoadBalancer, lb);

        getLocationDb(r).loadBalancers.push(lb);

        if (reserve_public_ip) {
            const ipAddress: IpAddress = {
                id: generateId(),
                address: randomPrivateIpv4(),
                user_id: getUserId(r),
                billing_account_id,
                type: "public",
                enabled: true,
                created_at,
                updated_at: created_at,
                uuid: generateUuid(),
                is_deleted: false,
                is_ipv6: false,
                assigned_to: lbUuid,
                assigned_to_resource_type: "load_balancer",
                assigned_to_private_ip: lbPrivateAddress,
            };
            console.debug("Creating mock IP for Load balancer: %o", { ipAddress });

            validateSpec(networkSpec.IpAddress, ipAddress);
            getLocationDb(r).ipAddresses.push(ipAddress);
        }
        markDirty();

        return HttpResponse.json(lb);
    }),
    http.patch("/{location}/network/load_balancers/{uuid}", async (r) => {
        const { display_name } = await parseJson(r, networkSpec.LoadBalancerRenameBody);

        const loadBalancer = getRequestLoadBalancer(r);
        loadBalancer.display_name = display_name;
        markDirty();
        validateSpec(networkSpec.LoadBalancer, loadBalancer);

        return HttpResponse.json(loadBalancer);
    }),
    http.put("/{location}/network/load_balancers/{uuid}/billing_account", async (r) => {
        const urlParams = getQueryParams(r);
        const loadBalancer = getRequestLoadBalancer(r);

        const billing_account_id = urlParams.has("set_id") ? Number(urlParams.get("set_id")) : undefined;
        if (billing_account_id) loadBalancer.billing_account_id = billing_account_id;
        markDirty();
        validateSpec(networkSpec.LoadBalancer, loadBalancer);

        return HttpResponse.json(loadBalancer);
    }),
    http.delete("/{location}/network/load_balancers/{uuid}", async (r) => {
        const loadBalancer = getRequestLoadBalancer(r);

        const db = getLocationDb(r);
        db.loadBalancers = db.loadBalancers.filter((lb) => lb.uuid !== loadBalancer.uuid);
        markDirty();

        return HttpResponse.json(true);
    }),
    http.post("/{location}/network/load_balancers/{uuid}/targets", async (r) => {
        const { target_type, target_uuid } = await parseJson(r, networkSpec.LbCreateTarget);

        const target: LbTarget = {
            created_at: getMockTimeTz(),
            target_uuid,
            target_type,
            target_ip_address: randomPrivateIpv4(),
        };

        validateSpec(networkSpec.LbTarget, target);

        const loadBalancer = getRequestLoadBalancer(r);
        loadBalancer.targets.push(target);
        markDirty();

        return HttpResponse.json(target);
    }),
    http.delete("/{location}/network/load_balancers/{uuid}/targets/{target_uuid}", async (r) => {
        const targetUuid = getParam(r, "target_uuid");
        const loadBalancer = getRequestLoadBalancer(r);
        loadBalancer.targets = loadBalancer.targets.filter((t) => t.target_uuid !== targetUuid);
        markDirty();

        return HttpResponse.json(true);
    }),
    http.post("/{location}/network/load_balancers/{uuid}/forwarding_rules", async (r) => {
        const { source_port, target_port } = await parseJson(r, networkSpec.LbCreateForwardingRule);

        const rule: LbForwardingRule = {
            protocol: "TCP",
            uuid: generateUuid(),
            created_at: getMockTimeTz(),
            source_port,
            target_port,
            settings: {
                connection_limit: 10000,
                session_persistence: "SOURCE_IP",
            },
        };

        validateSpec(networkSpec.LbForwardingRule, rule);

        const loadBalancer = getRequestLoadBalancer(r);
        loadBalancer.forwarding_rules.push(rule);
        markDirty();

        return HttpResponse.json(rule);
    }),
    http.delete("/{location}/network/load_balancers/{uuid}/forwarding_rules/{rule_uuid}", async (r) => {
        const ruleUuid = getParam(r, "rule_uuid");
        const loadBalancer = getRequestLoadBalancer(r);

        loadBalancer.forwarding_rules = loadBalancer.forwarding_rules.filter((r) => r.uuid !== ruleUuid);
        markDirty();

        return HttpResponse.json(true);
    }),
    //#endregion

    //#region Firewalls
    http.get("/{location}/network/firewalls", async (r) => {
        return HttpResponse.json(getLocationDb(r).firewalls);
    }),
    http.post("/{location}/network/firewalls", async (r) => {
        const {
            billing_account_id = defaultBillingAccountId,
            display_name,
            rules,
            resources_assigned = [],
        } = await parseJson(r, networkSpec.FirewallCreateBody);

        const fwUuid = generateUuid();

        const created_at = getMockTimeTz();
        const fw: Firewall = {
            uuid: fwUuid,
            display_name,
            user_id: getUserId(r),
            billing_account_id,
            created_at,
            rules: rules.map(({ direction, protocol, port_start, port_end, endpoint_spec_type, endpoint_spec }) => ({
                uuid: generateUuid(),
                direction,
                protocol,
                policy: "pass",
                port_start,
                port_end,
                endpoint_spec_type,
                endpoint_spec,
            })),
            resources_assigned,
        };
        console.debug("Creating mock firewall: %o", { fw });

        validateSpec(networkSpec.Firewall, fw);

        getLocationDb(r).firewalls.push(fw);

        markDirty();

        return HttpResponse.json(fw);
    }),
    http.put("/{location}/network/firewalls/{uuid}", async (r) => {
        const { display_name, billing_account_id, rules } = await parseJson(r, networkSpec.FirewallModifyBody);
        const firewall = getRequestFirewall(r);

        if (display_name) firewall.display_name = display_name;
        if (billing_account_id) firewall.billing_account_id = billing_account_id;
        if (rules != null) {
            for (const rule of rules) {
                if (!rule.uuid) {
                    rule.uuid = generateUuid();
                }
            }
            firewall.rules = rules;
        }

        markDirty();
        validateSpec(networkSpec.Firewall, firewall);

        return HttpResponse.json(firewall);
    }),
    http.delete("/{location}/network/firewalls/{uuid}", async (r) => {
        const firewall = getRequestFirewall(r);

        const db = getLocationDb(r);
        db.firewalls = db.firewalls.filter((f) => f.uuid !== firewall.uuid);
        markDirty();

        return HttpResponse.json(true);
    }),

    http.post("/{location}/network/firewalls/{uuid}/vms", async (r) => {
        const urlParams = getQueryParams(r);
        const vmUuid = urlParams.get("vm_uuid");

        const target: FirewallAssignedResource = {
            resource_type: "vm",
            resource_uuid: notNull(vmUuid),
        };

        validateSpec(networkSpec.FirewallAssignedResource, target);

        const firewall = getRequestFirewall(r);
        firewall.resources_assigned?.push(target);
        markDirty();
        validateSpec(networkSpec.Firewall, firewall);

        return HttpResponse.json(firewall.resources_assigned);
    }),
    http.delete("/{location}/network/firewalls/{uuid}/vms", async (r) => {
        const urlParams = getQueryParams(r);
        const vmUuid = urlParams.has("vm_uuid") ? urlParams.get("vm_uuid") : undefined;

        const firewall = getRequestFirewall(r);
        firewall.resources_assigned = firewall.resources_assigned?.filter((t) => t.resource_uuid !== vmUuid);

        markDirty();
        validateSpec(networkSpec.Firewall, firewall);

        return HttpResponse.json(true);
    }),
    //#endregion

    //#region Billing
    http.post("/{location}/user-resource/resource_billing", async (r) => {
        const db = getLocationDb(r);

        const { uuid, billing_account_id } = await parseJson(r, vmSpec.VmChangeBillingAccountBody);

        const vm = mustFindBy(db.vms, (v) => v.uuid, uuid, "VM");
        vm.billing_account = billing_account_id;

        markDirty();
        return HttpResponse.json(vm);
    }),

    http.get("/payment/billing_account/list", async (_r) => {
        return HttpResponse.json(getMockDb().billingAccounts);
    }),

    http.get("/payment/config/country_regions", async (_r) => {
        return HttpResponse.json(countryRegionsConfigExample);
    }),

    http.post("/payment/billing_account", async (r) => {
        const { client_email, site, title, type, ...body } = await parseForm(r, billingSpec.BillingAccountCreateFields);

        const billingAccount: BillingAccount = {
            ...body,

            id: generateId(),
            user_id: getUserId(r),
            created: getMockTimeEpoch(),

            title: notNull(title),
            site: notNull(site),
            email: notNull(client_email),

            ...createFieldValues,

            allow_debt: type === "invoice",
            restriction_level: "LIMITED",
            referral_share_code: randomLowerAlpha(),
            reseller: "",
            low_balance_notice_settings: {
                is_enabled: true,
            },
        };

        console.debug("Creating mock billing account: %o", billingAccount);
        validateSpec(billingSpec.BillingAccount, billingAccount);

        getMockDb().billingAccounts.push(billingAccount);
        markDirty();

        return HttpResponse.json(billingAccount);
    }),

    http.post("/payment/billing_account/set_default", async (r) => {
        const accountId = getBaIdQuery(r);
        for (const account of getMockDb().billingAccounts) {
            account.is_default = account.id === accountId;
        }
        markDirty();

        return HttpResponse.json({ success: true });
    }),

    http.post("/payment/billing_account/{id}/campaign_activate/{campaign_code}", async (r) => {
        const code = String(r.params.campaign_code);
        if (code === "valid") {
            return HttpResponse.json<CampaignActivateResponse>({ message: "123 EUR credit added" });
        } else if (code === "used") {
            throwStandardErrorResponse("Campaign code is already used.");
        }

        throwStandardErrorResponse("Invalid campaign.");
    }),

    http.put("/payment/billing_account/{id}/recurring_payment", async (r) => {
        const accountId = Number(r.params.id);
        const account = findById(getMockDb().billingAccounts, accountId);
        const body = await parseJson(r, billingSpec.AutomatedTopUpEnableDisableBody);

        account.is_recurring_payment_enabled = body.is_recurring_payment_enabled;

        if (body.is_recurring_payment_enabled) {
            account.recurring_payment_threshold = body.recurring_payment_threshold;
            account.recurring_payment_amount = body.recurring_payment_amount;
        }

        markDirty();

        return HttpResponse.json<AutomatedTopUpEnableDisableResponse>({ account });
    }),

    http.put("/payment/low_balance_notice_settings", async (r) => {
        const {
            billing_account_id: accountId,
            is_enabled,
            threshold,
        } = await parseJson(r, billingSpec.ModifyLowBalanceNoticeSettingsBody);
        const account = findById(getMockDb().billingAccounts, accountId);

        const noticeSettings = {
            is_enabled,
            threshold,
        };
        account.low_balance_notice_settings = noticeSettings;

        markDirty();

        return HttpResponse.json<ModifyLowBalanceNoticeSettingsResponse>(noticeSettings);
    }),

    http.post("/payment/apply_for_invoice_payment", async (r) => {
        const body = await parseJson(r, billingSpec.ApplyForInvoiceBody);
        const accountId = body.billing_account_id;

        const db = getMockDb();

        const account = findById(db.billingAccounts, accountId);

        const identifier = `mock_${randomAlphaNumeric(10)}`;
        const expirationDate = new Date(2124, 11);

        const existingPrimaryCard = db.billingCards.find(
            (c) => c.billing_account_id === accountId && !c.is_deleted && c.is_primary,
        );

        const cardData: BillingAccountCardData = {
            id: "N/A",
            type: "paying_by_invoice",
            card_type: "By invoice",
            expire_month: "12",
            expire_year: "2099",
            card_holder: account.email,
            client: {},
        };
        const card: BillingAccountCard = {
            additional_data: makeAdditionalCardData(cardData),
            billing_account_id: accountId,
            billing_account_processor_identifier_id: 1234,
            created: getMockTimeEpoch(),
            id: generateId(),
            identifier,
            is_deleted: false,
            is_primary: existingPrimaryCard === undefined,
            is_verification_pending: false,
            is_verified: false, // depends on payment component's ENV variable value env.INVOICE_PAYMENT_INITIAL_STATE == "active"
            processor: exampleInvoiceProcessor,
            valid_thru: expirationDate.getTime() / 1000,
        };

        account.can_pay = true;
        // XXX: Perhaps this should be looked up dynamically in the BA query?
        account.primary_card = cardData;

        db.billingCards.push(card);
        markDirty();

        return HttpResponse.json(card);
    }),

    http.get("/payment/invoice/list", async (r) => {
        const baId = getBaIdQuery(r);
        const ba = findById(getMockDb().billingAccounts, baId);
        const invoices = getMockDb().billingInvoices.filter((i) => i.billing_account_id === ba.id);
        return HttpResponse.json(invoices);
    }),

    http.get("/payment/invoice", async (r) => {
        const invoiceId = getInvoiceIdQuery(r);
        const invoice = findById(getMockDb().billingInvoices, invoiceId);
        return HttpResponse.json(invoice);
    }),

    http.get("/payment/card/list", async (r) => {
        const baId = getBaIdQuery(r);
        const _account = findById(getMockDb().billingAccounts, baId);
        const cards = getMockDb().billingCards.filter((c) => c.billing_account_id === baId);
        return HttpResponse.json(cards);
    }),

    http.put("/payment/card/set_primary", async (r) => {
        const paymentObjectId = getPaymentObjectIdQuery(r);
        for (const card of getMockDb().billingCards) {
            card.is_primary = card.id === paymentObjectId;
        }
        markDirty();

        return HttpResponse.json({ success: true });
    }),

    http.get("/payment/usage", async (r) => {
        const _baId = getBaIdQuery(r); // TODO: use this
        return HttpResponse.json(getMockDb().billingUsage);
    }),

    http.get("/payment/credit/list", async (r) => {
        const baId = getBaIdQuery(r);
        const ba = findById(getMockDb().billingAccounts, baId);
        const history = ba.status === "PENDING_VALIDATION" ? [] : getMockDb().billingHistory;
        return HttpResponse.json(history);
    }),

    http.get("/payment/admin/credit", async (_r) => {
        const history = getMockDb().billingHistory;
        return HttpResponse.json(history);
    }),

    http.post("/payment/credit/buy", async (r) => {
        const body = await parseJson(r, billingSpec.OmiseCreditBuyBody);
        const { payment_object_id, front_base_url } = body;
        invariant(payment_object_id === exampleVisaId, "Only Visa card is expected");

        const data_ref = `mock-credit-buy:${randomUpperAlphaNumeric()}`;
        if (body.target?.type === "invoice") {
            invoicePaymentsByDataRef.set(data_ref, body.target.invoice_id);
        }

        const message: z.infer<typeof topupVerifyMessage> = {
            type: "topup_verify",
            data_ref,
        };

        const response: OmiseCreditBuyResponse = {
            success: true,
            message: "",
            data: {
                id: "mock-id",
                object: "mock",
                status: "mock",
            },
            relay_props: {
                authorize_uri: buildVerifyUri(message, getReturnUrl(front_base_url, "credit_buy")),
                data_ref,
            },
        };
        return HttpResponse.json(response);
    }),

    http.post("/payment/link/send_data", async (r) => {
        const body = await parseJson(r, billingSpec.LinkSendDataBody);

        const { processor } = body;
        invariant(processor === "omise", "Only Omise processor is supported");
        if (body.target?.type === "invoice") {
            findById(getMockDb().billingInvoices, body.target.invoice_id).status = InvoiceStatusEnum.PAID;
            markDirty();
        }

        const params = JSON.parse(body.params) as LinkParams;
        const { paymentMethod } = params;
        invariant(paymentMethod === "promptpay", "Only PromptPay payment method is supported");

        console.debug("Payment link send data: %o", { body, params });

        const qrCodePath = (await import("@/modules/billing/payment/mock/qrcode.svg?url")).default;
        const qrcode_url = new URL(qrCodePath, window.location.origin).toString();

        return HttpResponse.json<LinkSendDataResponse>({
            success: true,
            error_msg: "",
            data: {
                charge_id: "123",
                charge_status: "pending",
                flow: "mock",
                persistent_data_ref: "mock-omise-link",
                qrcode_url,
            },
        });
    }),

    http.get("/payment/final_topup_amount", async (r) => {
        const { billing_account_id, processor, amount } = z
            .object({
                billing_account_id: zfd.numeric(z.number().int()),
                processor: z.string(),
                amount: zfd.numeric(z.number().min(0)),
            })
            .strict()
            .parse(Object.fromEntries(getQueryParams(r).entries()));

        invariant(billing_account_id);
        invariant(processor === "omise", "Only Omise processor is supported");
        // TODO: Actually calculate the final amount
        const final_amount = amount * 1.2;
        return HttpResponse.json<FinalAmountResponse>({ final_amount });
    }),

    http.post("/payment/link/save_methods", async (r) => {
        const body = await parseJson(r, billingSpec.SaveMethodsBody);
        const { billing_account_id, method_ids } = body;

        const ba = findById(getMockDb().billingAccounts, billing_account_id);
        ba.additional_data = JSON.stringify({
            link_methods: method_ids,
        });
        markDirty();

        return HttpResponse.json({ success: true });
    }),

    http.get("/payment/persistent_data", async (r) => {
        const params = getQueryParams(r);

        const baId = getBaIdQuery(r);
        const identifier = mustGet(params, "identifier", "params");

        const base = {
            billing_account_id: baId,
            created: getMockTimeEpoch(),
            data: "{}",
            id: 123,
            identifier,
        } satisfies Partial<PersistentDataResponse>;

        const [type] = identifier.split(":");

        switch (type) {
            case "mock-omise-link":
                return HttpResponse.json<PersistentDataResponse>({
                    ...base,
                    status: "success",
                    processor_method: "omise::promptpay",
                });

            case "mock-credit-buy": {
                const verified = verifiedTopUpIds.has(identifier);
                console.debug("Check omise topup status: %o", { identifier, verified });
                const paidInvoiceId = verified ? invoicePaymentsByDataRef.get(identifier) : undefined;
                if (paidInvoiceId != null) {
                    findById(getMockDb().billingInvoices, paidInvoiceId).status = InvoiceStatusEnum.PAID;
                    invoicePaymentsByDataRef.delete(identifier);
                    markDirty();
                }

                return HttpResponse.json<PersistentDataResponse>({
                    ...base,
                    status: verified ? "success" : "failed",
                    data: JSON.stringify(
                        (verified
                            ? {
                                  object: "charge",
                                  status: "successful",
                              }
                            : {
                                  object: "charge",
                                  status: "pending",
                              }) satisfies OmiseChargeData,
                    ),
                    processor_method: "omise_creditcard",
                });
            }

            default:
                unreachableSwitchCase(identifier);
        }
    }),

    http.post("/payment/card", async (r) => {
        const body = await parseJson(r, billingSpec.AddCardBody);
        const { billing_account_id, token } = body;
        console.debug("Mock add card: %o", { billing_account_id, token });

        const { number, expiration_month, expiration_year } = parseOmiseMockToken(token);
        const expirationDate = new Date(expiration_year, expiration_month - 1);
        const identifier = `mock_${randomAlphaNumeric(10)}`;
        const cardData: BillingAccountCardData = {
            id: identifier,
            expire_month: expiration_month,
            expire_year: expiration_year,
            type: "creditcard",
            card_type: "visa",
            last4: number.slice(-4),
        };
        const card: BillingAccountCard = {
            additional_data: makeAdditionalCardData(cardData),
            billing_account_id,
            billing_account_processor_identifier_id: 123,
            created: getMockTimeEpoch(),
            id: generateId(),
            identifier,
            is_deleted: false,
            is_primary: false,
            is_verification_pending: false,
            is_verified: false,
            // TODO: Add multi-processor support
            processor: exampleStripeProcessor,
            valid_thru: expirationDate.getTime() / 1000,
        };

        const account = findById(getMockDb().billingAccounts, billing_account_id);

        account.can_pay = true;
        // XXX: Perhaps this should be looked up dynamically in the BA query?
        account.primary_card = cardData;

        getMockDb().billingCards.push(card);
        markDirty();

        return HttpResponse.json(card);
    }),

    http.post("/payment/card/verify", async (r) => {
        const body = await parseJson(r, billingSpec.CardVerifyRequest);
        const { payment_object_id, front_base_url, force_3ds: _ } = body;

        const card = findById(getMockDb().billingCards, payment_object_id);
        card.is_verification_pending = true;

        const message: z.infer<typeof cardVerifyMessage> = {
            type: "card_verify",
            payment_object_id,
        };

        return HttpResponse.json<CardVerifyResponse>({
            card,
            authorize_uri: buildVerifyUri(message, getReturnUrl(front_base_url, "card_verify")),
        });
    }),

    http.delete("/payment/card", async (r) => {
        const paymentObjectId = getPaymentObjectIdQuery(r);
        const db = getMockDb();
        const theCard = db.billingCards.find((c) => c.id === paymentObjectId);
        const billingAccountId = theCard?.billing_account_id;
        const setNewPrimary = theCard?.is_primary;

        db.billingCards = db.billingCards.filter((c) => c.id !== paymentObjectId);

        const baCards = db.billingCards.filter((c) => c.billing_account_id === billingAccountId);

        let baCounter = 0;
        for (const card of baCards) {
            baCounter++;
            if (setNewPrimary) {
                card.is_primary = baCounter === baCards.length;
            }
        }

        markDirty();

        return HttpResponse.json({ success: true });
    }),

    //#endregion

    //#region Common data
    http.get("/config/payment/restriction_level_clear_topup_threshold", async (_r) => {
        // TODO: maybe create proper mock db object?
        return HttpResponse.json<RestrictionLevelClearTopupThreshold>({ restriction_level_clear_topup_threshold: 25 });
    }),

    http.get("/config/locations", async (_r) => {
        return HttpResponse.json(getMockDb().locations);
    }),

    http.get("/pricing/list/current", async (_r) => {
        return HttpResponse.json(getMockDb().pricing);
    }),

    http.get("/pricing/list", async (_r) => {
        return HttpResponse.json(getMockDb().pricingHistory);
    }),

    http.get("/{location}/user-resource/host_pool/list", async (r) => {
        return HttpResponse.json(getLocationDb(r).hostPools);
    }),
    //#endregion Common data

    //#region Internal requests
    http.post("/mock/error", async (_r) => {
        return HttpResponse.text("Mock Internal server error", { status: 500 });
    }),
    //#endregion

    ///// Imports
    ...adminHandlers,
    ...graphqlHandlers,
    ...metalHandlers,
];

/** Get the `authorize_uri` to which the payment method should eventually redirect (return) to. */
function getReturnUrl(front_base_url: string, flowId: string) {
    if (isStorybook) {
        // Use our custom mock HTML return page in Storybook environment
        return new URL(payment_return_htm_url, origin);
    }

    const returnUrl = new URL(front_base_url);
    returnUrl.pathname += `/return/${flowId}`;
    return returnUrl;
}

//#region Mock payment verification helpers
const cardVerifyMessage = z.object({
    type: z.literal("card_verify"),
    payment_object_id: z.number(),
});

const topupVerifyMessage = z.object({
    type: z.literal("topup_verify"),
    data_ref: z.string(),
});

const windowMessage = z.discriminatedUnion("type", [cardVerifyMessage, topupVerifyMessage]);

function buildVerifyUri(message: { type: string } | null, returnUrl: URL | string) {
    const authorizeUri = new URL(payment_verify_htm_url, origin);

    if (message != null) {
        authorizeUri.searchParams.set("message", JSON.stringify(message));
    }
    authorizeUri.searchParams.set("return_url", returnUrl.toString());

    // Let the front-end (popup) code know that even though this is a same-origin location, it should mock it as a foreign verification page
    authorizeUri.searchParams.set(mockVerifyPageMarker, "");

    return authorizeUri.toString();
}

/** Listens for "message" events from other windows (to eg. implement the mock popup payment flow) */
function windowMessageListener({ data }: MessageEvent<unknown>): void {
    if (!isObjectWithKey(data, "type")) {
        return;
    }
    console.debug("Received message: %o", data.type);

    const { data: message } = windowMessage.safeParse(data);
    switch (message?.type) {
        case "card_verify": {
            console.debug("Card verified: %o", message);
            const card = findById(getMockDb().billingCards, message.payment_object_id);
            card.is_verified = true;
            card.is_verification_pending = false;
            markDirty();
            break;
        }
        case "topup_verify": {
            console.debug("Topup verified: %o", message);
            verifiedTopUpIds.add(message.data_ref);
            break;
        }
    }
}

window.addEventListener("message", windowMessageListener);
import.meta.hot?.dispose(() => {
    window.removeEventListener("message", windowMessageListener);
});
//#endregion

//#region Network-specific accessors
function getRequestIpAddress(r: StandardRequest) {
    const db = getLocationDb(r);
    const address = getParam(r, "address");
    return mustFindBy(db.ipAddresses, (ip) => ip.address, address, "IP address");
}

function getRequestVpcNetwork(r: StandardRequest) {
    const db = getLocationDb(r);
    const uuid = getUuidParam(r);
    return mustFindBy(db.vpcNetworks, (n) => n.uuid, uuid, "VPC network");
}

function getRequestLoadBalancer(r: StandardRequest) {
    const db = getLocationDb(r);
    const uuid = getUuidParam(r);
    return mustFindBy(db.loadBalancers, (lb) => lb.uuid, uuid, "Load Balancer");
}

function getRequestFirewall(r: StandardRequest) {
    const db = getLocationDb(r);
    const uuid = getUuidParam(r);
    return mustFindBy(db.firewalls, (f) => f.uuid, uuid, "Firewall");
}

function unAssignIp(attachedIp: IpAddress) {
    delete attachedIp.assigned_to;
    delete attachedIp.assigned_to_resource_type;
    delete attachedIp.assigned_to_private_ip;
    attachedIp.unassigned_at = getMockTimeTz();
}
//#endregion

//#region Billing-specific accessors
function getBaIdQuery(r: StandardRequest) {
    const query = getQueryParams(r);
    return Number(mustGet(query, "billing_account_id", "query"));
}

function getPaymentObjectIdQuery(r: StandardRequest) {
    const query = getQueryParams(r);
    return Number(mustGet(query, "payment_object_id", "query"));
}

function getInvoiceIdQuery(r: StandardRequest) {
    const query = getQueryParams(r);
    return Number(mustGet(query, "invoice_id", "query"));
}
//#endregion
